/**
 * Transforms existing call script data format to CallScriptV2 format
 *
 * This utility helps convert the legacy script data structure to the new
 * card-based format used by CallScriptV2.
 */

/**
 * Get the form field name based on field type and attribute
 * @param {Object} field - Field object
 * @returns {string} Form field name
 */
const getFieldName = (field) => {
  const fieldTypeAttr = field.field_type?.attribute;
  const fieldAttr = field.attribute;

  // Special attribute-based field names
  const attributeFieldMap = {
    phone: 'phone_number',
    lead_email: 'email',
    lead_address: 'address',
    lead_source_type: 'leadType',
    lead_is_registered_patient: 'isRegister',
    lead_preferred_contact_method: 'preferredContactMethod',
    user_search: 'bookedWith',
    treatment_search: 'treatment',
    lead_search: 'leadName',
    capture_contact_details: 'captureContactDetails',
    confirm_speaker: 'confirmSpeaker',
    answer_the_call: 'answerTheCallCompletedBy',
    identify_caller: 'identifyCaller',
    reason_for_calling: 'reasonForCall',
    close_call: 'closeCall',
  };

  // Check both attribute sources (field_type.attribute and field.attribute)
  if (fieldTypeAttr && attributeFieldMap[fieldTypeAttr]) {
    return attributeFieldMap[fieldTypeAttr];
  }
  if (fieldAttr && attributeFieldMap[fieldAttr]) {
    return attributeFieldMap[fieldAttr];
  }

  return field.field_name || `field_${field.id}`;
};

/**
 * Transform a choice field based on ui_type
 * @param {Object} field - Field object
 * @returns {Object} Element configuration
 */
const transformChoiceField = (field) => {
  const uiType = field.field_type?.ui_type;
  const choices = field.field_type?.choices || field.choices || [];
  const attribute = field.field_type?.attribute || field.attribute;

  switch (uiType) {
    case 'DROPDOWN':
      return {
        type: 'select',
        field: getFieldName(field),
        id: field.id, // Include numeric field ID for API submissions
        label: field.label,
        placeholder: 'Select...',
        required: field.is_required,
        options: choices,
      };

    case 'MULTI_SELECT':
      return {
        type: 'choiceGrid',
        field: getFieldName(field),
        id: field.id,
        options: choices,
        choiceType: 'checkbox',
      };

    case 'CHECKBOX':
      // Special handling for registered patient checkbox
      if (attribute === 'lead_is_registered_patient') {
        return {
          type: 'choiceGrid',
          field: 'isRegister',
          id: field.id,
          options: choices.length > 0 ? choices : ['Yes'],
          choiceType: 'checkbox',
          single: true,
        };
      }
      return {
        type: 'choiceGrid',
        field: getFieldName(field),
        id: field.id,
        options: choices,
        choiceType: 'checkbox',
      };

    case 'RADIO':
      return {
        type: 'choiceGrid',
        field: attribute === 'lead_preferred_contact_method' ? 'preferredContactMethod' : getFieldName(field),
        id: field.id,
        options: choices,
        choiceType: 'radio',
        single: true,
      };

    default:
      return {
        type: 'select',
        field: getFieldName(field),
        id: field.id,
        label: field.label,
        placeholder: 'Select...',
        required: field.is_required,
        options: choices,
      };
  }
};

/**
 * Transform a char field based on attribute
 * @param {Object} field - Field object
 * @returns {Object} Element configuration
 */
const transformCharField = (field) => {
  const attribute = field.field_type?.attribute || field.attribute;

  switch (attribute) {
    case 'phone':
      return {
        type: 'textField',
        field: 'phone_number',
        id: field.id, // Include numeric field ID for API submissions
        label: field.label || 'Phone Number',
        placeholder: 'Enter phone number...',
        required: field.is_required,
      };

    case 'lead_email':
      return {
        type: 'textField',
        field: 'email',
        id: field.id,
        label: field.label || 'Email',
        placeholder: 'Enter email address...',
        required: field.is_required,
      };

    case 'lead_address':
      return {
        type: 'textField',
        field: 'address',
        id: field.id,
        label: field.label || 'Address',
        placeholder: 'Enter address...',
        required: field.is_required,
      };

    default:
      return {
        type: 'textField',
        field: getFieldName(field),
        id: field.id,
        label: field.label,
        placeholder: field.placeholder,
        required: field.is_required,
      };
  }
};

/**
 * Transform a legacy script section to V2 card format
 * @param {Object} section - Legacy section object
 * @param {number} sectionIndex - Section index (1-based)
 * @returns {Array} Array of card objects
 */
const transformSectionToCards = (section, sectionIndex, originalSectionId) => {
  const cards = [];
  const badgeClass = `s${((sectionIndex - 1) % 7) + 1}`;

  // Filter out fields where is_enabled is false (like original implementation)
  const enabledFields = section.fields?.filter((field) => field?.is_enabled);

  enabledFields?.forEach((field, fieldIndex) => {
    // Parse metadata (can be a JSON string or object)
    let parsedMetadata = null;
    if (field.metadata) {
      try {
        parsedMetadata = typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata;
      } catch (e) {
        parsedMetadata = null;
      }
    }

    const card = {
      section: sectionIndex,
      question: fieldIndex + 1,
      badge: `Section ${sectionIndex}.${fieldIndex + 1} · ${section.title || 'Section'}`,
      badgeClass,
      title: field.label || field.field_name || 'Question',
      subtitle: '',
      elements: [],
      // Store original section and field IDs for conditional logic matching
      originalSectionId: originalSectionId || section.id,
      fieldId: field.id,
      metadata: parsedMetadata,
    };

    // Transform field based on type
    const fieldType = field.field_type?.field_type || field.type;
    const fieldTypeAttr = field.field_type?.attribute;
    const fieldAttr = field.attribute;

    // Special attribute-based UI overrides
    // These attributes render custom components regardless of field_type
    // They can appear in either field.field_type.attribute or field.attribute
    const SPECIAL_ATTRIBUTES = [
      'capture_contact_details',
      'confirm_speaker',
      'answer_the_call',
      'identify_caller',
      'reason_for_calling',
      'close_call',
    ];

    // Check both attribute sources for special attribute match
    let specialAttr = null;
    if (fieldAttr && SPECIAL_ATTRIBUTES.includes(fieldAttr)) {
      specialAttr = fieldAttr;
    } else if (fieldTypeAttr && SPECIAL_ATTRIBUTES.includes(fieldTypeAttr)) {
      specialAttr = fieldTypeAttr;
    }
    const isSpecialAttribute = !!specialAttr;

    // Resolved attribute for non-special field logic (char/choice transforms, etc.)
    const attribute = fieldTypeAttr || fieldAttr;

    // Add "Say" block for description text
    // For 'label' type: the description IS the say text (handled in switch case)
    // For special attributes: always add description as say block (since switch case is skipped)
    // For other types: add description as say block if it exists
    if ((fieldType !== 'label' || isSpecialAttribute) && field.description) {
      card.elements.push({
        type: 'say',
        text: field.description,
        tag: 'Say',
      });
    }

    if (isSpecialAttribute) {
      card.elements.push({
        type: specialAttr,
        field: getFieldName(field),
        id: field.id,
        label: field.label || field.field_name || '',
        required: field.is_required,
      });
      if (card.elements.length > 0) {
        cards.push(card);
      }
      return; // Skip the normal field_type switch
    }

    switch (fieldType) {
      // Special field types from original implementation
      case 'lead_search_select':
        if (attribute === 'lead_search') {
          card.elements.push({
            type: 'lead_search_select',
            field: 'leadName',
            id: field.id, // Include numeric field ID for API submissions
            label: field.label || 'Search Lead',
            required: field.is_required,
            placeholder: 'Search for a lead...',
          });
        }
        break;

      case 'practice_treatment_search_select':
        if (attribute === 'treatment_search') {
          card.elements.push({
            type: 'practice_treatment_search_select',
            field: 'treatment',
            id: field.id,
            label: field.label || 'Treatment',
            required: field.is_required,
          });
        }
        break;

      case 'practice_user_search_select':
        if (attribute === 'user_search') {
          card.elements.push({
            type: 'practice_user_search_select',
            field: 'bookedWith',
            id: field.id,
            label: field.label || 'Assign to',
            required: field.is_required,
          });
        }
        break;

      case 'char':
        card.elements.push(transformCharField(field));
        break;

      case 'choice':
        card.elements.push(transformChoiceField(field));
        break;

      case 'boolean':
        card.elements.push({
          type: 'choiceGrid',
          field: getFieldName(field),
          id: field.id,
          options: ['Yes', 'No'],
          choiceType: 'radio',
          single: true,
        });
        break;

      case 'datetime':
        card.elements.push({
          type: 'date',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          required: field.is_required,
        });
        break;

      case 'text':
        card.elements.push({
          type: 'textarea',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          placeholder: field.placeholder,
          required: field.is_required,
          rows: 5,
        });
        break;

      case 'email':
      case 'phone':
        card.elements.push({
          type: 'textField',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          placeholder: field.placeholder,
          required: field.is_required,
        });
        break;

      case 'textarea':
      case 'long_text':
      case 'paragraph_text':
        card.elements.push({
          type: 'textarea',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          placeholder: field.placeholder,
          required: field.is_required,
          rows: 3,
        });
        break;

      case 'dropdown':
      case 'select':
        card.elements.push({
          type: 'select',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          placeholder: field.placeholder || 'Select...',
          required: field.is_required,
          options: field.options || [],
        });
        break;

      case 'checkbox':
      case 'multi_select':
        card.elements.push({
          type: 'choiceGrid',
          field: getFieldName(field),
          id: field.id,
          options: field.options || [],
          choiceType: 'checkbox',
        });
        break;

      case 'radio':
      case 'single_select':
        card.elements.push({
          type: 'choiceGrid',
          field: getFieldName(field),
          id: field.id,
          options: field.options || [],
          choiceType: 'radio',
          single: true,
        });
        break;

      case 'date':
        card.elements.push({
          type: 'date',
          field: getFieldName(field),
          id: field.id,
          label: field.label,
          required: field.is_required,
        });
        break;

      case 'label':
        // Labels show description text as a "say" block (no id needed, not a form field)
        if (field.description) {
          card.elements.push({
            type: 'say',
            text: field.description,
            tag: 'Say',
          });
        }
        break;

      case 'say':
        // Say blocks are not form fields, no id needed
        if (field.content || field.text) {
          card.elements.push({
            type: 'say',
            text: field.content || field.text,
            tag: 'Say',
          });
        }
        break;

      case 'tip':
      case 'hint':
        // Tips are not form fields, no id needed
        card.elements.push({
          type: 'tip',
          text: field.content || field.text,
        });
        break;

      default:
        // Default to text field if we have a label
        if (field.label) {
          card.elements.push({
            type: 'textField',
            field: getFieldName(field),
            id: field.id,
            label: field.label,
            placeholder: field.placeholder,
            required: field.is_required,
          });
        }
        break;
    }

    // Only add card if it has elements
    if (card.elements.length > 0) {
      cards.push(card);
    }
  });

  return cards;
};

/**
 * Transform legacy script data to V2 format
 * @param {Object} scriptData - Legacy script data
 * @param {string} type - Script type ('inbound' or 'outbound')
 * @returns {Object} V2 script data format
 */
export const transformScriptData = (scriptData, type = 'inbound') => {
  if (!scriptData || !scriptData.sections) {
    return {
      id: scriptData?.id, // Preserve original script ID even for empty scripts
      title: scriptData?.title || 'Call Script',
      subtitle: '',
      sections: [],
      cards: [],
    };
  }

  const sections = [];
  let allCards = [];

  scriptData.sections.forEach((section, index) => {
    const sectionId = index + 1;
    const rawSectionName = section.name || section.title || `Section ${sectionId}`;
    // Check if section is marked as default (name ends with $default)
    const isDefaultSection = rawSectionName.endsWith('$default');
    // Strip $default suffix for display name
    const sectionName = isDefaultSection ? rawSectionName.replace(/\$default$/, '').trim() : rawSectionName;
    // Keep track of original section ID from API for conditional logic matching
    const originalSectionId = section.id;

    // Parse section metadata for conditionLogic
    let sectionConditionLogic = null;
    if (section.metadata?.conditionLogic) {
      sectionConditionLogic = section.metadata.conditionLogic;
    } else if (section.conditionLogic) {
      sectionConditionLogic = section.conditionLogic;
    }

    sections.push({
      id: sectionId,
      name: sectionName,
      originalId: originalSectionId, // Preserve original ID for conditional logic
      conditionLogic: sectionConditionLogic, // Section-level conditional logic
      isDefaultSection, // Track if this is a default section (shown when no treatment)
    });

    const sectionCards = transformSectionToCards({ ...section, title: sectionName }, sectionId, originalSectionId);
    allCards = [...allCards, ...sectionCards];
  });

  // Mark last card
  if (allCards.length > 0) {
    allCards[allCards.length - 1].isLast = true;
  }

  return {
    id: scriptData.id, // Preserve original script ID for API submissions
    title: scriptData.name || scriptData.title || (type === 'inbound' ? 'Inbound Enquiry Script' : 'Outbound Follow-up Script'),
    subtitle: type === 'inbound'
      ? 'Patient calling in · Capture their enquiry'
      : 'Following up on enquiry · Book appointment',
    sections,
    cards: allCards,
    // Preserve conditional logic rules for dynamic field visibility
    conditionalLogics: scriptData.conditional_logics || [],
    // Preserve treatments list for treatment change detection (avoids unnecessary script reload)
    treatments: scriptData.treatments || [],
  };
};

export default transformScriptData;
